import { useEffect, useMemo, useState } from 'react';
import { setMilliseconds, sub } from 'date-fns';
import styled from 'styled-components';

import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import { FilterSection } from 'components/shared/filters/FilterSlideout';
import DatePicker from 'components/shared/form/DatePicker';
import { FILTER_TYPES } from 'constants/filterConfigs';
import AIPrompt from 'models/AIPrompt';
import { useInfiniteAiPrompts } from 'services/api/aiPrompts';
import { SummarizationsFilterState as Filters, useSummarizationsStore } from 'stores/summarizationsStore';

import DatePickerShortcuts from '../insights/filterBar/DatePickerShortcuts';

type SummarizationsFilterBarProps = {
  onApplyFilters: (filters: { search: string; [key: string]: any }) => void;
};

const SummarizationsFilterBar = ({ onApplyFilters }: SummarizationsFilterBarProps) => {
  const filters = useSummarizationsStore((state) => state.filters);
  const { setFilters } = useSummarizationsStore((state) => state.actions);

  // We track the filter dates separately from the selected datepicker values so that we can delay
  // updating the filter state until the datepicker closes. Otherwise, if the user is updating date part
  // values in the datepicker via the keyboard, we'd send an API request for every character typed in.
  const now = setMilliseconds(new Date(), 0);
  const thirtyDaysAgo = sub(now, { days: 30 });
  const createdAtFilterValue = filters.createdAt as unknown as Date[] | undefined;
  const startDateFilterValue = createdAtFilterValue?.[0] || thirtyDaysAgo;
  const endDateFilterValue = createdAtFilterValue?.[1] || now;
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(startDateFilterValue);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(endDateFilterValue);
  const [filteredStartDate, setFilteredStartDate] = useState<Date>(startDateFilterValue);
  const [filteredEndDate, setFilteredEndDate] = useState<Date>(endDateFilterValue);
  const maxDate = useMemo(() => new Date(), []);

  const handleClearFilters = () => {
    setFilters({});
  };

  useEffect(() => {
    if (!filteredStartDate && !filteredEndDate) {
      const newFilters = { ...filters };
      delete newFilters.createdAt;
      setFilters(newFilters);
    } else {
      setFilters({
        ...filters,
        createdAt: [filteredStartDate || new Date('1900-01-01'), filteredEndDate || new Date('2999-01-01')] as any,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilters, filteredStartDate, filteredEndDate]);

  const promptQueryParams = useMemo(
    () => ({
      'displayName.distinct': 'true',
      type: 'summary',
      targetClassname: 'Summarization',
    }),
    []
  );

  const filterSections: FilterSection[] = useMemo(
    () => [
      {
        title: 'Prompt Type',
        filters: [
          {
            filterType: FILTER_TYPES.DROPDOWN,
            name: 'promptDisplayName',
            placeholder: 'Type',
            getOptions: {
              hook: useInfiniteAiPrompts,
              params: promptQueryParams,
            },
            getOptionLabel: (obj: AIPrompt) => obj?.displayName ?? '',
            getOptionValue: (obj: AIPrompt) => obj?.displayName ?? '',
          },
        ],
      },
    ],
    [promptQueryParams]
  );

  const handlePromptTypeChange = (value) => {
    setFilters({ ...filters, promptDisplayName: value });
  };

  const DatePickerFooter = ({ dateInput }) => (
    <DatePickerShortcuts
      startDate={selectedStartDate!}
      endDate={selectedEndDate!}
      datePickerRef={dateInput}
      setDates={([startDate, endDate]) => {
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);
        setFilteredStartDate(startDate);
        setFilteredEndDate(endDate);
      }}
    />
  );

  return (
    <FilterBar
      searchPlaceholder='Search'
      showFiltersButton={false}
      showResults={false}
      applyFilters={(filters: Filters) => {
        onApplyFilters({ search: '', ...filters });
      }}
      filters={filters}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title='Summary History'
      quickFilter={
        <FilterMultiSelect
          name='promptType'
          placeholder='Type'
          getOptions={{
            hook: useInfiniteAiPrompts,
            params: promptQueryParams,
          }}
          getOptionLabel={(obj: AIPrompt) => obj?.displayName ?? ''}
          getOptionValue={(obj: AIPrompt) => obj?.displayName ?? ''}
          value={filters.promptDisplayName || []}
          onChange={handlePromptTypeChange}
        />
      }
      callToAction={
        <DatePickerContainer>
          <DatePicker
            data-gtm-id='referralsStartDateFilter'
            onCalendarClose={() => setFilteredStartDate(selectedStartDate)}
            onBlur={() => setFilteredStartDate(selectedStartDate)}
            onChange={(val) => setSelectedStartDate(val as Date)}
            selected={selectedStartDate}
            maxDate={selectedEndDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />

          <Emdash>&mdash;</Emdash>

          <DatePicker
            data-gtm-id='referralsEndDateFilter'
            onCalendarClose={() => setFilteredEndDate(selectedEndDate)}
            onBlur={() => setFilteredEndDate(selectedEndDate)}
            onChange={(val) => setSelectedEndDate(val as Date)}
            selected={selectedEndDate}
            maxDate={maxDate}
            minDate={selectedStartDate ?? undefined}
            renderFooter={DatePickerFooter}
            popperPlacement='bottom-start'
          />
        </DatePickerContainer>
      }
    />
  );
};

export default SummarizationsFilterBar;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 160px;

    @media ${({ theme }) => theme.devices.desktop} {
      max-width: 140px;
    }
  }
`;

const Emdash = styled.span`
  margin: 0 8px;
`;
